import { Link, usePage } from "@inertiajs/react"
import { ChevronLeft } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { tagSlug } from "@/lib/tags"
import { blogPostingNode, breadcrumbNode, faqPageNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section } from "@/components/marketing/sections/Section"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { track } from "@/lib/analytics"

type RelatedPost = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  cover_image_url: string | null
  reading_time?: number
}

type Post = RelatedPost & {
  author: string | null
  published_at: string | null
  updated_at: string | null
  reading_time: number
  tags: string[]
  faq: { question: string; answer: string }[]
  body_html: string
  seo_title: string
  meta_description: string
}

export default function BlogShow({ post, related }: { post: Post; related: RelatedPost[] }) {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")
  const updated = isUpdated(post.published_at, post.updated_at) ? post.updated_at : null

  const meta = [
    post.author,
    formatDate(post.published_at),
    post.reading_time ? `${post.reading_time} min read` : null,
  ].filter(Boolean)

  return (
    <MarketingLayout>
      <SeoHead
        title={post.seo_title}
        description={post.meta_description}
        type="article"
        image={post.cover_image_url || undefined}
        jsonLd={[
          blogPostingNode(base, post),
          breadcrumbNode(base, [
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
          ...(post.faq?.length
            ? [faqPageNode(post.faq.map((f) => ({ q: f.question, a: f.answer })))]
            : []),
        ]}
      />

      <Section containerClassName="max-w-3xl">
        <Link
          href="/blog"
          className="inline-flex items-center gap-1 text-sm text-ink-muted no-underline hover:text-ink-display"
        >
          <ChevronLeft className="h-4 w-4" />
          All posts
        </Link>

        <article className="mt-6">
          <h1 className="display-2">{post.title}</h1>
          <p className="mt-4 text-sm text-ink-muted">{meta.join(" · ")}</p>
          {updated && (
            <p className="mt-1 text-xs text-ink-muted">Updated {formatDate(updated)}</p>
          )}

          {post.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <Link
                  key={tag}
                  href={`/blog/tag/${tagSlug(tag)}`}
                  className="no-underline"
                >
                  <Badge tone="muted">{tag}</Badge>
                </Link>
              ))}
            </div>
          )}

          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="mt-8 w-full rounded-xl border border-hairline object-cover"
            />
          )}

          <div
            className="body-content mt-8"
            dangerouslySetInnerHTML={{ __html: post.body_html }}
          />
        </article>

        {/* Convert readers into leads */}
        <aside className="mt-14 rounded-2xl border border-hairline bg-surface p-8 text-center sm:p-10">
          <h2 className="display-2">Ready to build authority through better conversations?</h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-body">
            FullCast is a 6-month, done-for-you podcast growth partnership for
            established experts — strategy, production, and the relationships that
            build trust.
          </p>
          <Button asChild size="lg" className="mt-6">
            <Link href="/apply" onClick={() => track("CTA Click")}>
              Apply for a Podcast Growth Fit Call
            </Link>
          </Button>
        </aside>

        {related.length > 0 && (
          <section className="mt-14 border-t border-hairline pt-10">
            <h2 className="display-2">Keep reading</h2>
            <div className="mt-8 grid gap-8 sm:grid-cols-2">
              {related.map((r) => (
                <article key={r.id} className="group">
                  <Link href={`/blog/${r.slug}`} className="block no-underline">
                    <div className="aspect-[16/9] overflow-hidden rounded-xl border border-hairline bg-surface">
                      {r.cover_image_url ? (
                        <img
                          src={r.cover_image_url}
                          alt={r.title}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center text-ink-muted">
                          <span className="font-display text-sm">FullCast</span>
                        </div>
                      )}
                    </div>
                    <h3 className="mt-4 transition-colors group-hover:text-accent-display">
                      {r.title}
                    </h3>
                    {r.excerpt && <p className="mt-2 text-ink-muted">{r.excerpt}</p>}
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}
      </Section>
    </MarketingLayout>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return ""
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// True when the post was meaningfully updated after publishing (different day).
function isUpdated(published: string | null, updated: string | null) {
  if (!published || !updated) return false
  return new Date(updated).toDateString() !== new Date(published).toDateString()
}
